/* eslint-disable @typescript-eslint/ban-ts-comment */
/* eslint-disable @typescript-eslint/prefer-nullish-coalescing */
import {
  DeliveryType,
  type IntercityOrder,
  LocationLevel,
  SERVICE_REQUESTS,
  findAdminFn,
  getAddressLabels,
  type DefaultQueryError,
  type FindAdmin,
  type OptimizedRoutesResponse,
  type OrderDetail,
  type Path,
  type Service,
  type User,
  OrderStatus,
} from '@ahm/api-wrappers-core';
import { toast } from '@ahm/react-ui';
import dayjs from 'dayjs';
import { jwtDecode, type JwtPayload } from 'jwt-decode';
import {
  DEFAULT_MAX_STOP_HM,
  DEFAULT_MAX_STOP_INTERCITY,
  DEFAULT_MAX_STOP_POINTS,
  ORDER_STATUS_COLORS,
} from './constants';
import { ROUTES } from '@/config/router';
import {
  type Reward,
  RewardPolicyType,
} from '@/features/loyalty/api/reward/types';

interface CustomJwtPayload extends JwtPayload {
  partner?: string; // Define the partner field as optional
}

export const getMaxStopPointsConfig = (
  service?: Service | null,
  userProfile?: User
): number => {
  if (service?.delivery_type === DeliveryType.Nationwide) {
    return DEFAULT_MAX_STOP_INTERCITY;
  }
  if (service?.delivery_type === 'TRUCK-HM') {
    return DEFAULT_MAX_STOP_HM;
  }
  if (service?.delivery_type === DeliveryType.Truck) {
    return DEFAULT_MAX_STOP_POINTS;
  }

  const serviceMaxStopPoints =
    service?.max_stop_points ?? DEFAULT_MAX_STOP_POINTS;
  const userProfileMaxStopPoint = userProfile?.max_stop_points ?? 0;
  return serviceMaxStopPoints > 1
    ? Math.max(serviceMaxStopPoints, userProfileMaxStopPoint)
    : serviceMaxStopPoints;
};

export function routeOptimization_getCombinations(
  rest: number[],
  soFar: number[],
  output: number[][]
) {
  let next;
  let remaining;

  if (rest.length == 0) {
    output.push(soFar);
  } else {
    for (let i = 0; i < rest.length; i++) {
      remaining = subArr(rest, 0, i).concat(
        subArr(rest, i + 1, rest.length - 1)
      );
      next = soFar.concat(rest[i]);
      routeOptimization_getCombinations(remaining, next, output);
    }
  }

  function subArr(rest: number[], from_index: number, length: number) {
    const r: number[] = [];
    for (let i = from_index; i < from_index + length; i++) {
      if (rest[i]) {
        r.push(rest[i]);
      }
    }

    return r;
  }
}

interface RouteCase {
  path: number[];
  duration: number;
}

export function routeOptimization_getTheShortestRoute(
  optimizedData: OptimizedRoutesResponse | null,
  paths: Path[]
): RouteCase | undefined {
  if (!optimizedData) return undefined;
  const table = optimizedData.durations;
  if (!table) return undefined;

  const waypointIndexes: number[] = [];
  for (let i = 1; i < paths.length; ++i) {
    waypointIndexes.push(i);
  }

  const combinations: number[][] = [];
  routeOptimization_getCombinations(waypointIndexes, [], combinations);

  for (const item of combinations) {
    item.unshift(0);
  }

  const allCases: RouteCase[] = [];
  for (const comb of combinations) {
    let sumDurations = 0;
    for (let idx = 0; idx < comb.length - 1; idx++) {
      const x = comb[idx];
      const y = comb[idx + 1];
      if (table?.[x]?.[y]) {
        sumDurations += table[x][y];
      }
    }

    allCases.push({
      path: comb,
      duration: sumDurations,
    });
  }
  return allCases.reduce((prev, curr) => {
    return prev && prev.duration < curr.duration ? prev : curr;
  });
}

export function genBuildFields<T extends object>(object: T, keys: string[]) {
  return Object.entries(object).reduce((acc, [key, value]) => {
    if (keys.includes(key)) acc[key as keyof typeof acc] = value;
    return acc;
  }, {} as T);
}

export const convertPaymentMethodNumber = (name: string) => {
  return name.slice(-5);
};

export const payWithEWallet = (methodType: string): boolean => {
  return methodType === 'e_wallet' || methodType === 'card';
};

export function objectToQuery(
  obj: Record<string, string | string[] | number | undefined>
) {
  let query = '';
  for (const key in obj) {
    if (obj[key]) {
      const value = obj[key];
      if (Array.isArray(value)) {
        value.forEach(function (item) {
          query += key + '=' + encodeURIComponent(item) + '&';
        });
      } else {
        // @ts-ignore
        query += key + '=' + encodeURIComponent(value) + '&';
      }
    }
  }
  return query.slice(0, -1);
}

export const fetchLocationInfo = async (
  lat: number,
  lng: number
): Promise<{
  provinceInfo: FindAdmin | undefined;
  districtInfo: FindAdmin | undefined;
  wardInfo: FindAdmin | undefined;
}> => {
  try {
    const res = await findAdminFn({
      point: { latitude: lat, longitude: lng },
    });

    if (res && res.length > 0) {
      const provinceInfo = res.find(
        (res) => res.level === LocationLevel.Province
      );
      const districtInfo = res.find(
        (res) => res.level === LocationLevel.District
      );
      const wardInfo = res.find((res) => res.level === LocationLevel.Ward);

      return { provinceInfo, districtInfo, wardInfo };
    }

    return {
      provinceInfo: undefined,
      districtInfo: undefined,
      wardInfo: undefined,
    };
  } catch (err) {
    const errorData = (err as DefaultQueryError).response?.data;
    toast.error({
      title: errorData?.title,
      message: errorData?.message,
      withIcon: true,
    });
    // notifications.show({
    //   title: errorData?.title,
    //   message: errorData?.message,
    //   color: 'red',
    // });

    console.error('Error fetching location info:', err);
    throw err;
  }
};

export const getServiceType = (pathname: string) => {
  return {
    isNationwide: pathname === ROUTES.nationwide,
    isTruckService: pathname === ROUTES.truck,
    isHMService: pathname === ROUTES.houseMoving,
    isBikeService: pathname === ROUTES.newOrder || pathname === ROUTES.home,
  };
};
export function getBrandName(reward?: Reward): string {
  if (reward?.type === RewardPolicyType.URBOX) {
    return reward?.extra?.brand ?? 'Urbox';
  } else {
    return 'Ahamove';
  }
}

export const convertTimezone = (time: Date) => {
  const timeZone = dayjs.tz.guess();
  return dayjs.tz(time, timeZone);
};

export const findInsuranceInRequests = (service: Service | null) => {
  if (!service || service?.requests?.length === 0) return null;

  return service?.requests?.find((request) =>
    request?.type?.includes(SERVICE_REQUESTS.COD_INSURANCE)
  );
};

export const isServiceRestrictedByBan = (service: Service): boolean => {
  if (!service?.truck_ban?.enable || !service?.truck_ban?.ban_hours?.length) {
    return false;
  }

  const currentTime = dayjs();
  return service.truck_ban.ban_hours.some(({ from, to }) => {
    return (
      from <= currentTime.hour() &&
      (to > currentTime.hour() ||
        (to === currentTime.hour() && currentTime.minute() === 0))
    );
  });
};

export const areAllServicesRestrictedByBan = (services: Service[]): boolean => {
  if (!services?.length) return false;

  return services.every((service) => isServiceRestrictedByBan(service));
};

export const getTotalLengthWidthHeight = (data) => {
  const invalidPackageDetails = data.some(
    (item) => !item.weight || !item.height || !item.width || !item.length
  );
  const initialData = { length: 0, width: 0, height: 0 };
  if (invalidPackageDetails) return initialData;
  return data.reduce((acc, item) => {
    acc.length += item.length * (item.quantity || 1);
    acc.width += item.width * (item.quantity || 1);
    acc.height += item.height * (item.quantity || 1);
    return acc;
  }, initialData);
};

export const checkPartnerFromToken = (token: string): boolean => {
  try {
    const decodedToken = jwtDecode<CustomJwtPayload>(token);
    return Boolean(decodedToken?.partner);
  } catch (error) {
    console.error('Error decoding token:', error);
    return false;
  }
};

export function transformPath(path: Path): Path {
  const baseFields = [
    'address',
    'lat',
    'lng',
    'name',
    'mobile',
    'building',
    'adr_source',
    'alt_mobile',
    'cod',
    'remarks',
    'inspection_policies',
    'tracking_number',
    'nationwide_province_id',
    'nationwide_district_id',
    'nationwide_ward_id',
    'nationwide_tracking_number',
  ];

  const filteredData = Object.entries(path).reduce((obj, [key, value]) => {
    if (baseFields.includes(key) && value != null) {
      obj[key] = value;
    }
    return obj;
  }, {} as Path);

  const [first_label, second_label] = getAddressLabels(path.address ?? '');

  return {
    ...filteredData,
    first_label,
    second_label,
  };
}

// Package detail utility functions
export const getWeightRange = (weight: number) => {
  if (weight <= 5) return { min: 0, max: 5 };
  if (weight <= 30) return { min: 5.1, max: 30 };
  if (weight <= 40) return { min: 30.1, max: 40 };
  if (weight <= 60) return { min: 40.1, max: 60 };
  if (weight <= 80) return { min: 60.1, max: 80 };
  return { min: 80.1, max: Infinity };
};

export const getDimensionsBySize = (size: string) => {
  const sizeMap: Record<
    string,
    { length: number; width: number; height: number }
  > = {
    S: { length: 25, width: 32, height: 12 },
    M: { length: 50, width: 40, height: 50 },
    L: { length: 60, width: 50, height: 60 },
    XL: { length: 70, width: 60, height: 70 },
    '2XL': { length: 90, width: 70, height: 90 },
    '3XL': { length: 90, width: 70, height: 90 },
  };
  return (
    `${sizeMap[size]?.length}x${sizeMap[size]?.width}x${sizeMap[size]?.height}` ||
    '25 x 32 x 12'
  );
};

export const getPackageDetailDimensions = (
  bikePackageSize: string | null | undefined,
  bikePackageWeight: number | null | undefined,
  itemDescriptions: { keyword: string }[]
) => {
  if (!bikePackageSize || !bikePackageWeight) return null;

  const dimensions = getDimensionsBySize(bikePackageSize);
  const weightRange = getWeightRange(bikePackageWeight);

  return {
    package_weight: weightRange,
    weight_kg: bikePackageWeight,
    package_size: bikePackageSize,
    package_dimensions_cm: dimensions,
    package_info: itemDescriptions[0]?.keyword,
  };
};

export const formatPathsData = (paths: Path[]) => {
  return [
    {
      sender_address: paths[0]?.address,
      sender_lat: paths[0]?.lat,
      sender_lng: paths[0]?.lng,
      sender_name: paths[0]?.name,
      sender_phone: paths[0]?.mobile,
    },
    {
      receiver_address: paths[1]?.address,
      receiver_lat: paths[1]?.lat,
      receiver_lng: paths[1]?.lng,
      receiver_name: paths[1]?.name,
      receiver_phone: paths[1]?.mobile,
    },
  ];
};

export const formatChatBotData = (
  paths: Path[],
  bikePackageSize: string | null | undefined,
  bikePackageWeight: number | null | undefined,
  itemDescriptions: { keyword: string }[],
  serviceId: string | null | undefined,
  orderTime: number | null | undefined,
  serviceTypes: Service[] | null
) => {
  const packageDetails = getPackageDetailDimensions(
    bikePackageSize,
    bikePackageWeight,
    itemDescriptions
  );

  const selectedService =
    serviceTypes?.find((s) => s._id === serviceId) ?? null;

  return {
    path: paths,
    package_info: packageDetails?.package_info,
    package_weight_kg: packageDetails?.weight_kg,
    package_dimensions_cm: packageDetails?.package_dimensions_cm,
    package_size: packageDetails?.package_size,
    selected_service: {
      serviceId: selectedService?._id,
      service_name: selectedService?.name,
      service_description: selectedService?.description_vi_vn,
      max_cod: selectedService?.max_cod,
      max_distance: selectedService?.max_distance,
    },
    ...(orderTime && orderTime !== 0 && { order_time: orderTime }),
  };
};

/**
 * Checks if a given longitude is valid.
 * @param lng The longitude to check.
 * @returns {boolean} True if the longitude is between -180 and 180.
 */
const isValidLongitude = (lng: number): boolean => {
  return Number.isFinite(lng) && lng >= -180 && lng <= 180;
};

/**
 * Checks if a given latitude is valid.
 * @param lat The latitude to check.
 * @returns {boolean} True if the latitude is between -90 and 90.
 */
const isValidLatitude = (lat: number): boolean => {
  return Number.isFinite(lat) && lat >= -90 && lat <= 90;
};

/**
 * Validates a coordinate pair based on the specified order.
 *
 * @param coords - The coordinate pair to validate, e.g., `[10.76, 106.66]`.
 * @param order - The expected order of coordinates, either 'lat-lng' (default) or 'lng-lat'.
 * @returns `true` if the coordinates are valid, `false` otherwise.
 */
export const isValidCoordinates = (
  coords: unknown,
  order: 'lat-lng' | 'lng-lat' = 'lat-lng'
): boolean => {
  if (!Array.isArray(coords) || coords.length !== 2) return false;

  return order === 'lng-lat'
    ? isValidLongitude(coords[0]) && isValidLatitude(coords[1])
    : isValidLatitude(coords[0]) && isValidLongitude(coords[1]);
};

/**
 * Recursively formats coordinate fields in an object to 'lat-lng' order.
 * This function mutates the original object.
 *
 * @param data - The object or array to process.
 * @param path - The path to the coordinate field, e.g., 'entrances.coordinates'.
 */
const formatPath = (data: any, path: string) => {
  const pathParts = path.split('.');

  const process = (currentData: any, parts: string[]) => {
    if (!currentData || parts.length === 0) {
      return;
    }

    const key = parts[0];
    const remainingParts = parts.slice(1);

    if (Array.isArray(currentData)) {
      currentData.forEach((item) => process(item, parts));
    } else if (
      typeof currentData === 'object' &&
      currentData !== null &&
      key in currentData
    ) {
      if (remainingParts.length === 0) {
        // This is the target coordinate field
        if (
          currentData[key] &&
          !isValidCoordinates(currentData[key], 'lat-lng')
        ) {
          currentData[key] = [currentData[key][1], currentData[key][0]];
        }
      } else {
        // This is a nested path, continue recursion
        process(currentData[key], remainingParts);
      }
    }
  };

  process(data, pathParts);
};

export const formatCoordinatesToLatLng = <T>(data: T, fields: string[]): T => {
  const dataClone = JSON.parse(JSON.stringify(data)); // Deep clone to avoid mutation
  fields.forEach((field) => formatPath(dataClone, field));
  return dataClone;
};
export function reuseOrder(
  order: OrderDetail | IntercityOrder,
  router?: any
): string | null {
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;

  if (!order) {
    toast.error({
      description: 'Không có thông tin đơn hàng để sử dụng lại',
    });
    return null;
  }

  const deliveryType = order.delivery_type || order.service?.delivery_type;

  if (!deliveryType) {
    toast.error({
      description: 'Không thể sử dụng lại đường đi và thông tin',
    });
    return null;
  }

  const routeMap = {
    [DeliveryType.Nationwide]: ROUTES.nationwide,
    [DeliveryType.Truck]: ROUTES.truck,
  };

  const route =
    routeMap[deliveryType as DeliveryType] || isMobile
      ? ROUTES.newOrder
      : ROUTES.home;
  const fullRoute = `${route}?order_id=${order._id}`;

  if (router) {
    router.push(fullRoute);
  }

  return fullRoute;
}

export const checkOrderClosed = (status: OrderStatus): boolean => {
  const closedStatuses = [
    OrderStatus.Cancelled,
    OrderStatus.Completed,
    OrderStatus.Failed,
  ];
  return closedStatuses.includes(status);
};

export const getOrderStatusConfig = (
  status: string | OrderStatus,
  t: (key: string) => string
) => {
  const key = typeof status === 'string' ? status : OrderStatus[status];

  const colors = ORDER_STATUS_COLORS[key] || {
    color: 'neutral.6',
    bg: 'neutral.1',
  };

  return {
    label: t(`order_status.${key}`),
    ...colors,
  };
};
